import { useEffect, useRef } from 'react';

/**
 * Reference counter for body scroll lock.
 * Multiple panels can request scroll lock; body is only unlocked when all release.
 */
let lockCount = 0;
let originalOverflow = '';

function lockScroll() {
  if (lockCount === 0) {
    originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  lockCount++;
}

function unlockScroll() {
  lockCount--;
  if (lockCount <= 0) {
    lockCount = 0;
    document.body.style.overflow = originalOverflow;
  }
}

/**
 * Hook to manage body scroll lock with reference counting.
 *
 * When multiple nested panels are open, only the first panel locks scroll
 * and only when the last panel closes is scroll restored.
 *
 * @param shouldLock - Whether this component wants to lock scroll
 *
 * @example
 * ```tsx
 * function Panel({ open }) {
 *   useBodyScrollLock(open);
 *   return open ? <div>Panel content</div> : null;
 * }
 * ```
 */
export function useBodyScrollLock(shouldLock: boolean) {
  const wasLockedRef = useRef(false);

  useEffect(() => {
    if (shouldLock && !wasLockedRef.current) {
      lockScroll();
      wasLockedRef.current = true;
    } else if (!shouldLock && wasLockedRef.current) {
      unlockScroll();
      wasLockedRef.current = false;
    }

    return () => {
      if (wasLockedRef.current) {
        unlockScroll();
        wasLockedRef.current = false;
      }
    };
  }, [shouldLock]);
}

/**
 * Get the current lock count (useful for debugging).
 */
export function getScrollLockCount(): number {
  return lockCount;
}

/**
 * Force reset scroll lock (emergency cleanup).
 * Use sparingly - only for edge cases like hot module replacement.
 */
export function resetScrollLock(): void {
  lockCount = 0;
  document.body.style.overflow = '';
}
